import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import { syncSubjectsWithStudents } from './SyncSubjects';

const PromoteClassModal = ({ isOpen, onClose, classId, currentClass, students, subjects, onSuccess }) => {
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [className, setClassName] = useState(currentClass?.name || '');
  const [loading, setLoading] = useState(false);
  const [teacherAssignments, setTeacherAssignments] = useState({}); // subjectId -> teacherId mapping

  // Fetch current teacher assignments for this class
  useEffect(() => {
    const fetchTeacherAssignments = async () => {
      if (!isOpen || !classId) return;

      try {
        const usersSnap = await getDocs(collection(db, 'users'));
        const assignments = {};

        usersSnap.docs.forEach(doc => {
          const teacher = { id: doc.id, ...doc.data() };
          if (['Teacher', 'Cashier', 'Admin'].includes(teacher.status) && teacher.isActive === true) {
            const teachingAssignments = teacher.teachingAssignments || [];
            teachingAssignments.forEach(assignment => {
              if (assignment.classId === classId) {
                assignments[assignment.subjectId] = teacher.id;
              }
            });
          }
        });

        setTeacherAssignments(assignments);
      } catch (error) {
        console.error("Error fetching teacher assignments:", error);
      }
    };

    fetchTeacherAssignments();
  }, [isOpen, classId]);

  // Fetch sessions and terms when modal opens
  useEffect(() => {
    const fetchSessionsAndTerms = async () => {
      if (!isOpen) return;
      
      try {
        console.log("Fetching sessions and terms...");
        
        // Fetch sessions - check both possible collection names
        let sessionsData = [];
        try {
          const sessionsSnap = await getDocs(collection(db, 'sessions'));
          sessionsData = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("Sessions fetched:", sessionsData);
        } catch (error) {
          console.error("Error fetching from 'sessions' collection:", error);
          // Try alternative collection name
          try {
            const sessionsSnap = await getDocs(collection(db, 'session'));
            sessionsData = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log("Sessions fetched from 'session':", sessionsData);
          } catch (err) {
            console.error("Error fetching from 'session' collection:", err);
          }
        }
        
        setSessions(sessionsData);
        
        // Fetch terms - check both possible collection names
        let termsData = [];
        try {
          const termsSnap = await getDocs(collection(db, 'terms'));
          termsData = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("Terms fetched:", termsData);
        } catch (error) {
          console.error("Error fetching from 'terms' collection:", error);
          // Try alternative collection name
          try {
            const termsSnap = await getDocs(collection(db, 'term'));
            termsData = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            console.log("Terms fetched from 'term':", termsData);
          } catch (err) {
            console.error("Error fetching from 'term' collection:", err);
          }
        }
        
        setTerms(termsData);
        
        // If current class has session/term, preselect them
        if (currentClass?.session) {
          const matchingSession = sessionsData.find(s => s.id === currentClass.session);
          if (matchingSession) {
            setSelectedSession(currentClass.session);
          }
        }
        
        if (currentClass?.term) {
          const matchingTerm = termsData.find(t => t.id === currentClass.term);
          if (matchingTerm) {
            setSelectedTerm(currentClass.term);
          }
        }
        
      } catch (error) {
        console.error("Error in fetchSessionsAndTerms:", error);
      }
    };

    fetchSessionsAndTerms();
  }, [isOpen, currentClass]);

  // Fetch current fees for the class
  const getCurrentFeesForClass = () => {
    if (!students.length) return 0;
    
    // Get fees from first student (assuming all students in class have same fees)
    const firstStudent = students[0];
    const enrollment = firstStudent.enrollments?.find(e => e.classId === classId);
    return enrollment?.fees || 0;
  };

  const handlePromote = async () => {
    if (!selectedSession || !selectedTerm || !className) {
      alert('Please select a session, term, and provide a class name.');
      return;
    }

    const currentFees = getCurrentFeesForClass();

    setLoading(true);
    try {
      // Create new class with subjects and teacher assignments preserved
      const newClassData = {
        name: className,
        section: currentClass.sectionId || currentClass.section || '',
        session: selectedSession,
        term: selectedTerm,
        formMaster: currentClass.formMaster?.id || currentClass.formMaster || '',
        students: students.map(student => student.id),
        subjects: subjects.map(subject => ({ 
          id: subject.id, 
          teacherId: teacherAssignments[subject.id] || '' 
        })),
      };

      const newClassRef = await addDoc(collection(db, 'classes'), newClassData);
      const newClassId = newClassRef.id;

      const psychomotorSkills = {
        handwriting: 'A',
        fluency: 'A',
        games: 'A',
        punctuality: 'A',
        neatness: 'A',
        politeness: 'A',
        cooperation: 'A',
        leadership: 'A',
        attentiveness: 'A',
        honesty: 'A',
      };

      // Update student enrollments with preserved fees and teacher assignments
      await Promise.all(
        students.map(async (student) => {
          const studentRef = doc(db, 'students', student.id);
          const studentDoc = await getDoc(studentRef);
          const studentData = studentDoc.data();

          const subjectsWithScores = subjects.map(subject => ({
            subjectId: subject.id,
            teacherId: teacherAssignments[subject.id] || '',
            scores: {
              firstca: 0,
              secondca: 0,
              exam: 0,
            },
          }));

          const updatedEnrollments = [
            ...(studentData.enrollments || []),
            {
              classId: newClassId,
              subjects: subjectsWithScores,
              psychomotorSkills,
              fees: currentFees, // Preserve the fee amount from current class
              paid: 0, // Reset paid amount for new class
              Attendance: [],
            },
          ];

          await updateDoc(studentRef, { enrollments: updatedEnrollments });
        })
      );

      // Update teacher assignments for the new class
      const usersSnap = await getDocs(collection(db, 'users'));
      
      await Promise.all(
        usersSnap.docs.map(async (userDoc) => {
          const teacher = { id: userDoc.id, ...userDoc.data() };
          if (['Teacher', 'Cashier', 'Admin'].includes(teacher.status) && teacher.isActive === true) {
            const oldAssignments = teacher.teachingAssignments || [];
            
            // Find which subjects this teacher taught in the old class
            const subjectsToCarryOver = [];
            subjects.forEach(subject => {
              if (teacherAssignments[subject.id] === teacher.id) {
                subjectsToCarryOver.push(subject.id);
              }
            });
            
            if (subjectsToCarryOver.length > 0) {
              // Add new assignments for the promoted class
              const newAssignments = [
                ...oldAssignments,
                ...subjectsToCarryOver.map(subjectId => ({
                  classId: newClassId,
                  subjectId: subjectId
                }))
              ];
              
              await updateDoc(doc(db, 'users', teacher.id), {
                teachingAssignments: newAssignments
              });
            }
          }
        })
      );

      // Sync subjects with students
      await syncSubjectsWithStudents(newClassId);

      onSuccess();
    } catch (error) {
      console.error("Error promoting class:", error);
      alert('Failed to promote class. Please try again.');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Promote Class"
      className="modal"
      overlayClassName="modal-overlay"
    >
      <div className="modal-content">
        <h2>Promote Class: {currentClass?.name}</h2>
        <div className="modal-form">
          <label className="text-left">Class Name</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter new class name"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
          />
          <label className="text-left">Select Session</label>
          <select
            className="input-field"
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
          >
            <option value="">Select a session</option>
            {sessions.map(session => (
              <option key={session.id} value={session.id}>
                {session.name}
              </option>
            ))}
          </select>
          <label className="text-left">Select Term</label>
          <select
            className="input-field"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <option value="">Select a term</option>
            {terms.map(term => (
              <option key={term.id} value={term.id}>
                {term.name}
              </option>
            ))}
          </select>
          <div className="row align-center justify-center gap-15">
            <button
              className="login-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="login-button"
              onClick={handlePromote}
              disabled={loading}
            >
              {loading ? 'Promoting...' : 'Promote Class'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PromoteClassModal;